import { FunctionComponent } from "react";

import { cx } from "@/utils";
import Typography from "@components/Typography";
import * as RadixTooltip from "@radix-ui/react-tooltip";

import styles from "./Tooltip.module.css";

/**
 * Tooltip proxy
 */

type TooltipProps = {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  content: RadixTooltip.TooltipContentProps["children"];
  passThrough?: boolean;
  asChild?: boolean;
  side?: "top" | "bottom" | "left" | "right";
  delayDuration?: number;
} & Omit<RadixTooltip.TooltipTriggerProps, "content">;

const Tooltip: FunctionComponent<TooltipProps> = ({
  children,
  passThrough = false,
  content,
  open,
  defaultOpen,
  onOpenChange,
  side = "top",
  className,
  asChild = true,
  delayDuration = 700,
  ...props
}) => {
  if (passThrough) return <>{children}</>;

  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
      >
        <RadixTooltip.Trigger
          asChild={asChild}
          className={cx({ [styles.ownTrigger]: asChild === false }, className)}
          {...props}
        >
          {children}
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content className={styles.root} side={side}>
            <Typography variant="body-2" align="center" margin="none">
              {content}
            </Typography>
            <RadixTooltip.Arrow />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};

export default Tooltip;
